import {
  getAccessToken,
  getPersistedClientPortalFlags,
  mergeClientPortalFlags,
  persistClientPortalFlags,
} from "../utils/auth";
import { getUserRole } from "../utils/sessionStore";
import { isClientPortalUser } from "../utils/isClientPortalUser";
import {
  can,
  defaultLandingPath,
  hospitalRoleLabel,
  NAV_PERMISSION_BY_KEY,
  normalizeHospitalRole,
} from "../utils/userDashboardPermissions";
import { getClientEmail } from "../utils/getClientEmail";
import { useAuth } from "../context/AuthContext";
import api from '../utils/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Outlet, Link, NavLink } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Tooltip,
} from '@mui/material';
import { FiMenu, FiLogOut, FiUser, FiX, FiCpu, FiArrowLeft, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { MdAccountCircle } from 'react-icons/md';
import {
  MdDashboard,
  MdBarChart,
  MdMeetingRoom,
  MdScience,
  MdCreditCard,
  MdAssessment,
  MdPeople,
} from 'react-icons/md';
import theme from './theme';
import { USER_APP_ROBOTS_PATH } from '../User-app/paths';
import {
  isUserPortalAppPath,
  resolvePathFromPaymentContext,
  resolveRobotNavPathFromLoginExtras,
  resolveRobotNavPathFromSubscription,
} from '../utils/clientAppRedirect';
import { fetchSubscriptionStatus } from '../services/userSubscriptionApi';
import logoImg from '../assets/spotless-logo-white.png';
import '../HomePage/Header.css';

/** Same pattern as HomePage Header — distinct color per nav label */
const CLIENT_NAV_ICON_COLORS = {
  Overview: '#2563eb',
  Operations: '#7c3aed',
  Rooms: '#0891b2',
  Efficacy: '#059669',
  Reports: '#d97706',
  Subscription: '#db2777',
  Members: '#4f46e5',
  Robot: '#0d9488',
};

/** Drawer width — matches HomePage Header.js */
const DRAWER_WIDTH = 320;

/** Same total offset as HomePage `Header` + `headerSpacer` (toolbar + gradient strip) */
const HEADER_OFFSET = '72px';

/** Handles wrapped payloads; client users may not have rows in admin-details. */
function parseAdminDetailsBody(data) {
  if (!data || typeof data !== 'object') return { name: '', role: '' };
  const inner = data.user ?? data.data ?? data.admin ?? data.record;
  const src = inner && typeof inner === 'object' ? { ...data, ...inner } : data;
  const name = src.name ?? src.userName ?? src.fullName ?? src.displayName ?? '';
  const role = src.role ?? '';
  return { name: String(name).trim(), role: String(role).trim() };
}

/** Matches `/client/subscription` payload: `userSubscriptionDetails.purchaseType` (e.g. `purchaseuser`). */
function normalizeClientPurchaseType(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

/** One line under role; `norm` is from `normalizeClientPurchaseType`. */
function isNavItemActive(item, pathname) {
  if (!item.path || item.path === '/') return pathname === '/';
  return pathname.startsWith(item.path);
}

function purchaseTypeDisplayLabel(norm) {
  if (norm === undefined || norm === '') return null;
  if (norm === 'purchaseuser') return 'Purchase user';
  if (norm === 'subscriptionuser') return 'Subscription user';
  return norm.charAt(0).toUpperCase() + norm.slice(1);
}

/**
 * `sub` is the tab segment appended to the dashboard base path. In the client
 * portal the base is `/client`; in the admin read-only view it is
 * `/admin/users/:email`. `clientOnly` items (e.g. the User-app Robot page) only
 * appear for the logged-in client, never in the admin view.
 */
const NAV_ITEMS = [
  { label: 'Overview', sub: 'overview', icon: <MdDashboard size={20} />, permission: NAV_PERMISSION_BY_KEY.overview },
  { label: 'Reports', sub: 'reports', icon: <MdAssessment size={20} />, navHint: 'Disinfection evidence & hygiene verify', permission: NAV_PERMISSION_BY_KEY.reports },
  { label: 'Operations', sub: 'operations', icon: <MdBarChart size={20} />, permission: NAV_PERMISSION_BY_KEY.operations },
  { label: 'Rooms', sub: 'rooms', icon: <MdMeetingRoom size={20} />, permission: NAV_PERMISSION_BY_KEY.rooms },
  { label: 'Efficacy', sub: 'efficacy', icon: <MdScience size={20} />, permission: NAV_PERMISSION_BY_KEY.efficacy },
  { label: 'Subscription', sub: 'subscription', icon: <MdCreditCard size={20} />, permission: NAV_PERMISSION_BY_KEY.subscription },
  { label: 'Members', sub: 'members', icon: <MdPeople size={20} />, permission: NAV_PERMISSION_BY_KEY.members },
  { label: 'Robot', clientOnly: true, clientPath: USER_APP_ROBOTS_PATH, icon: <FiCpu size={20} />, sidebarOnly: true, permission: NAV_PERMISSION_BY_KEY.robot },
];

/** Mirrors Header.js `styles` for sidebar / header bar */
const headerBarSx = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  zIndex: 1100,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
};

const headerRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 24px',
  maxWidth: '100%',
  margin: '0 auto',
};

const menuButtonSx = {
  background: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)',
  borderRadius: '12px',
  padding: '8px',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.35)',
  '&:hover': {
    background: 'linear-gradient(to right, #2563eb 0%, #db2777 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(236, 72, 153, 0.4)',
  },
};

/**
 * Shared dashboard shell. In `adminView` mode an AdminController views another
 * user's dashboard (read-only); the target email comes from the `:email` route
 * param and nav links point at `/admin/users/:email/*`.
 */
export default function UserDashboardLayout({ adminView = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { session, isLoading: authLoading, logout } = useAuth();
  const [clientPortalFlags, setClientPortalFlags] = useState(() => {
    const persisted = getPersistedClientPortalFlags();
    return {
      allowUserAppOnWeb: Boolean(persisted?.allowUserAppOnWeb),
      allowAccessWithoutPayment: Boolean(persisted?.allowAccessWithoutPayment),
      canAccessRobots: Boolean(persisted?.canAccessRobots),
    };
  });
  const [clientPortalFlagsLoaded, setClientPortalFlagsLoaded] = useState(
    () => Boolean(getPersistedClientPortalFlags())
  );

  const routeEmail = adminView ? decodeURIComponent(params.email || '') : '';
  /** Identity used for every data fetch on this dashboard. */
  const clientEmail = adminView ? routeEmail : getClientEmail();
  /** Base path for nav tabs in this mode. */
  const basePath = adminView ? `/admin/users/${encodeURIComponent(routeEmail)}` : '/client';

  const resolveNavPath = (item) => {
    if (item.clientOnly) return item.clientPath;
    return `${basePath}/${item.sub}`;
  };

  useEffect(() => {
    const role = getUserRole();
    if (adminView) {
      if (!['AdminController', 'Hr'].includes(role)) {
        navigate('/admin-dashboard', { replace: true });
      }
    } else if (!isClientPortalUser()) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [navigate, adminView]);

  const personaRole = adminView ? 'ADMIN' : normalizeHospitalRole(getUserRole());

  useEffect(() => {
    if (adminView) return;
    const path = location.pathname;
    if (isUserPortalAppPath(path) || path.startsWith('/user/subscription')) return;

    const match = NAV_ITEMS.find((item) => {
      if (item.clientOnly) return false;
      const itemPath = `${basePath}/${item.sub}`;
      return path === itemPath || path.startsWith(`${itemPath}/`);
    });
    if (!match) {
      // /client index → overview
      if (path === basePath || path === `${basePath}/`) return;
      return;
    }
    if (match.permission && !can(personaRole, match.permission)) {
      navigate(defaultLandingPath(personaRole), { replace: true });
    }
  }, [adminView, location.pathname, navigate, basePath, personaRole]);

  useEffect(() => {
    if (adminView) {
      setClientPortalFlagsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/client/login-redirect');
        if (cancelled) return;
        if (res.data?.success) {
          const nextFlags = mergeClientPortalFlags(res.data, getPersistedClientPortalFlags());
          setClientPortalFlags(nextFlags);
          persistClientPortalFlags(nextFlags);
        } else {
          setClientPortalFlags({
            allowUserAppOnWeb: Boolean(session.allowUserAppOnWeb),
            allowAccessWithoutPayment: Boolean(session.allowAccessWithoutPayment),
            canAccessRobots: Boolean(session.canAccessRobots),
          });
        }
      } catch {
        if (!cancelled) {
          const persisted = getPersistedClientPortalFlags();
          setClientPortalFlags({
            allowUserAppOnWeb: Boolean(session.allowUserAppOnWeb || persisted?.allowUserAppOnWeb),
            allowAccessWithoutPayment: Boolean(
              session.allowAccessWithoutPayment || persisted?.allowAccessWithoutPayment
            ),
            canAccessRobots: Boolean(session.canAccessRobots || persisted?.canAccessRobots),
          });
        }
      } finally {
        if (!cancelled) setClientPortalFlagsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminView, session.allowUserAppOnWeb, session.allowAccessWithoutPayment, session.canAccessRobots]);
  const [hospitalInfo, setHospitalInfo] = useState(null);
  const [userDetails, setUserDetails] = useState({ name: '', role: '' });
  /** `undefined` = not loaded yet; normalized string after `/client/subscription` (empty if unknown). */
  const [clientPurchaseTypeNorm, setClientPurchaseTypeNorm] = useState(undefined);

  /** Same as HomePage Header: `toggleSidebar(open)` */
  const toggleSidebar = (open) => () => {
    setSidebarOpen(open);
  };

  useEffect(() => {
    const fetchHospital = async () => {
      try {
        const email = clientEmail;
        if (!email) return;
        const res = await api.get(`/client/hospital-info?email=${encodeURIComponent(email)}`);
        if (res.data?.success) setHospitalInfo(res.data.hospital);
      } catch (e) {
        /* silently degrade */
      }
    };
    fetchHospital();
  }, [clientEmail]);

  useEffect(() => {
    const fetchClientPurchaseType = async () => {
      const email = clientEmail;
      if (!email) {
        setClientPurchaseTypeNorm('');
        return;
      }
      try {
        const res = await api.get(`/client/subscription?email=${encodeURIComponent(email)}`);
        if (res.data?.success) {
          const raw = res.data?.userSubscriptionDetails?.purchaseType;
          setClientPurchaseTypeNorm(normalizeClientPurchaseType(raw));
        } else {
          setClientPurchaseTypeNorm('');
        }
      } catch {
        setClientPurchaseTypeNorm('');
      }
    };
    fetchClientPurchaseType();
  }, [clientEmail]);

  /**
   * Name / role: `getemail-admin-details` matches HomePage Header (admins).
   * Client portal users (`role === "user"`) often have no admin row — then we use
   * `user-name/:email` (same as CreateRobotForUser) and finally the email local-part.
   */
  useEffect(() => {
    const fetchUser = async () => {
      const token = getAccessToken();
      const email = clientEmail;
      if (!token || !email) return;

      // In admin view the viewed account is a client user, not the admin.
      const roleFromLogin = adminView ? 'user' : getUserRole();
      let name = '';
      let role = roleFromLogin;

      // Client accounts store the hospital/account name on `user-name`, not admin-details.
      try {
        const res = await api.get(`user-name/${encodeURIComponent(email)}`);
        const raw = res.data?.name ?? res.data;
        const n = typeof raw === 'string' ? raw : raw?.name;
        if (n != null && String(n).trim()) name = String(n).trim();
      } catch {
        /* optional backend route */
      }

      if (!name) {
        try {
          const res = await api.get(`getemail-admin-details/${encodeURIComponent(email)}`);
          const p = parseAdminDetailsBody(res.data);
          if (p.name) name = p.name;
          if (p.role) role = p.role;
        } catch {
          /* Expected for many client accounts */
        }
      } else {
        try {
          const res = await api.get(`getemail-admin-details/${encodeURIComponent(email)}`);
          const p = parseAdminDetailsBody(res.data);
          if (p.role) role = p.role;
        } catch {
          /* role optional for client accounts */
        }
      }

      if (!name && email.includes('@')) {
        name = email.split('@')[0];
      }

      setUserDetails({ name, role });
    };
    fetchUser();
  }, [clientEmail, adminView]);

  const hideSubscriptionForPurchaseUser = clientPurchaseTypeNorm === 'purchaseuser';
  const canUseUserApp =
    clientPortalFlags.allowUserAppOnWeb ||
    Boolean(session.allowUserAppOnWeb) ||
    clientPortalFlags.canAccessRobots ||
    Boolean(session.canAccessRobots);
  const portalAccessResolved =
    adminView ||
    clientPortalFlagsLoaded ||
    (!authLoading && (Boolean(session.allowUserAppOnWeb) || Boolean(session.canAccessRobots)));

  useEffect(() => {
    if (!hideSubscriptionForPurchaseUser) return;
    if (location.pathname.startsWith(`${basePath}/subscription`)) {
      navigate(`${basePath}/overview`, { replace: true });
    }
  }, [hideSubscriptionForPurchaseUser, location.pathname, navigate, basePath]);

  useEffect(() => {
    // User-app / payment pages require portal access (never in admin view).
    // Subscription checkout/plans must stay reachable before robots access is granted.
    const isSubscriptionPaymentPath = location.pathname.startsWith('/user/subscription');
    if (adminView || !portalAccessResolved || canUseUserApp) return;
    if (isUserPortalAppPath(location.pathname)) {
      if (isSubscriptionPaymentPath) return;
      navigate('/client/overview', { replace: true });
    }
  }, [adminView, portalAccessResolved, canUseUserApp, location.pathname, navigate]);

  const isUserAppRoute = isUserPortalAppPath(location.pathname);

  const hasEfficacy = hospitalInfo?.hasEfficacyTesting !== false;
  const visibleNav = NAV_ITEMS
    .filter((t) => {
      if (t.clientOnly && (adminView || !canUseUserApp)) return false;
      if (hideSubscriptionForPurchaseUser && t.sub === 'subscription') return false;
      if (t.conditional && !hasEfficacy) return false;
      if (!adminView && t.permission && !can(personaRole, t.permission)) return false;
      // Support view: hide subscription + members
      if (adminView && (t.sub === 'subscription' || t.sub === 'members')) return false;
      return true;
    })
    .map((t) => ({ ...t, path: resolveNavPath(t) }));

  /** Main content tab bar — excludes drawer-only items (e.g. Robot) */
  const headerNav = visibleNav.filter((t) => !t.sidebarOnly);

  /** Longest path first so `/client/operations` matches Operations, not Overview */
  const activeNavItem =
    [...visibleNav].sort((a, b) => b.path.length - a.path.length).find((item) => isNavItemActive(item, location.pathname)) ?? headerNav[0];

  const activeHeaderNavIndex = (() => {
    const matches = headerNav
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => isNavItemActive(item, location.pathname))
      .sort((a, b) => b.item.path.length - a.item.path.length);
    return matches[0]?.index ?? 0;
  })();
  const activeHeaderNavItem = headerNav[activeHeaderNavIndex] ?? headerNav[0];
  const canGoPrevTab = activeHeaderNavIndex > 0;
  const canGoNextTab = activeHeaderNavIndex < headerNav.length - 1;

  const goToAdjacentTab = (direction) => {
    const nextIndex = activeHeaderNavIndex + direction;
    if (nextIndex < 0 || nextIndex >= headerNav.length) return;
    navigate(headerNav[nextIndex].path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  /** Robot nav: uses GET /user/subscription/status (same rules as login redirect). */
  const handleRobotNavClick = async (e) => {
    e.preventDefault();
    setSidebarOpen(false);

    const allowAccessWithoutPayment =
      clientPortalFlags.allowAccessWithoutPayment ||
      Boolean(session.allowAccessWithoutPayment);

    try {
      const statusRes = await fetchSubscriptionStatus();
      if (statusRes.data?.success) {
        const ctx = statusRes.data.paymentContext;
        navigate(
          resolvePathFromPaymentContext(ctx, { allowAccessWithoutPayment })
        );
        return;
      }
    } catch {
      /* fall through */
    }

    try {
      const res = await api.get('/client/login-redirect');
      if (res.data?.success) {
        navigate(
          resolveRobotNavPathFromLoginExtras({
            redirectRoute: res.data.redirectRoute,
            purchaseType: res.data.purchaseType,
            paymentFlow: res.data.paymentFlow,
            canAccessRobots: res.data.canAccessRobots,
            allowAccessWithoutPayment:
              res.data.allowAccessWithoutPayment ?? allowAccessWithoutPayment,
          })
        );
        return;
      }
    } catch {
      /* try subscription payload below */
    }

    try {
      const email = clientEmail;
      if (email) {
        const subRes = await api.get(`/client/subscription?email=${encodeURIComponent(email)}`);
        if (subRes.data?.success) {
          const usd = subRes.data.userSubscriptionDetails || {};
          navigate(
            resolveRobotNavPathFromSubscription({
              purchaseType: usd.purchaseType,
              plans: usd.plans,
              allowAccessWithoutPayment,
            })
          );
          return;
        }
      }
    } catch {
      /* fall through */
    }

    navigate(USER_APP_ROBOTS_PATH);
  };

  // In admin view the footer action returns to the user list and keeps the admin
  // session intact (it must never clear the admin's auth).
  const handleExit = () => navigate('/admin/users');

  const displayName = (userDetails.name ?? '').toString().trim();
  const rawRole = (userDetails.role ?? '').toString().trim();
  const displayRole = adminView
    ? (rawRole === 'user' || !rawRole ? 'Hospital account' : hospitalRoleLabel(rawRole))
    : hospitalRoleLabel(rawRole || personaRole);
  const purchaseTypeLine = purchaseTypeDisplayLabel(clientPurchaseTypeNorm);

  const outletContext = {
    hospitalInfo,
    hasEfficacy,
    clientPurchaseTypeNorm,
    clientEmail,
    adminView,
    personaRole: adminView ? null : personaRole,
    clinicalRedacted: Boolean(adminView),
    userDetails: { name: displayName, role: displayRole, purchaseType: purchaseTypeLine },
  };

  /** Sidebar panel — close control shown whenever used inside Drawer (like Header.js) */
  const sidebarInner = (showClose) => (
    <Box
      className="sidebar-glass udb-sidebar-inner"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Sidebar header — same as Header.js drawer */}
      <Box
        sx={{
          padding: '32px 24px 20px 24px',
          background: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)',
          borderRadius: showClose ? '12px' : '0 12px 0 0',
          color: 'white',
          position: 'relative',
        }}
      >
        {showClose && (
          <IconButton
            onClick={toggleSidebar(false)}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '5px',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
                transform: 'scale(1.05)',
              },
            }}
            className="focusable"
            aria-label="Close sidebar"
          >
            <FiX size={18} color="white" />
          </IconButton>
        )}
        <Box sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              background: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)',
              width: 60,
              height: 60,
              fontSize: '1.5rem',
              margin: '0 auto 16px auto',
              border: '2px solid rgba(255,255,255,0.35)',
            }}
          >
            {displayName ? displayName.charAt(0).toUpperCase() : <MdAccountCircle size={30} />}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 0.5, fontSize: '1.25rem' }}>
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500, fontSize: '0.875rem' }}>
            {displayRole}
          </Typography>
          {purchaseTypeLine ? (
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.72)',
                fontWeight: 500,
                fontSize: '0.8125rem',
                mt: 0.75,
              }}
            >
              {purchaseTypeLine}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Divider
        sx={{
          margin: '20px 0',
          backgroundColor: 'rgba(100, 116, 139, 0.2)',
        }}
      />

      <Box sx={{ flex: 1, padding: '24px', minHeight: 0, overflowY: 'auto' }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: '#64748b',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.1em',
            marginBottom: '16px',
            textTransform: 'uppercase',
          }}
        >
          NAVIGATION
        </Typography>
        <List sx={{ padding: 0 }}>
          {visibleNav.map((item) => {
            const iconColor = CLIENT_NAV_ICON_COLORS[item.label] ?? '#64748b';
            const isRobotNav = item.label === 'Robot';
            const active = isRobotNav
              ? isUserPortalAppPath(location.pathname)
              : isNavItemActive(item, location.pathname);
            return (
              <ListItem
                button
                key={item.path}
                component={isRobotNav ? 'div' : Link}
                to={isRobotNav ? undefined : item.path}
                onClick={isRobotNav ? handleRobotNavClick : toggleSidebar(false)}
                className="menu-item-hover"
                selected={active}
                sx={{
                  borderRadius: '12px',
                  margin: '4px 0',
                  padding: '12px 16px',
                  color: active ? theme.colors.brandPrimary : '#475569',
                  transition: 'all 0.3s ease',
                  ...(active && {
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(236, 72, 153, 0.08) 100%)',
                  }),
                  '&:hover': {
                    background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                    transform: 'translateX(8px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: active ? theme.colors.brandPrimary : iconColor,
                  }}
                >
                  {React.cloneElement(item.icon, { color: active ? theme.colors.brandPrimary : iconColor })}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.navHint || null}
                  primaryTypographyProps={{
                    sx: { fontWeight: 500, fontSize: '0.875rem' },
                  }}
                  secondaryTypographyProps={{
                    sx: { fontSize: '0.7rem', color: '#94a3b8' },
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          padding: '24px',
          borderTop: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <Button
          startIcon={adminView ? <FiArrowLeft size={16} /> : <FiLogOut size={16} />}
          onClick={() => {
            setSidebarOpen(false);
            if (adminView) {
              handleExit();
            } else {
              handleLogout();
            }
          }}
          fullWidth
          sx={{
            background: adminView
              ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderRadius: '12px',
            padding: '9px 18px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: adminView
                ? 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)'
                : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              transform: 'translateY(-2px)',
              boxShadow: adminView
                ? '0 8px 25px rgba(99, 102, 241, 0.3)'
                : '0 8px 25px rgba(239, 68, 68, 0.3)',
            },
          }}
        >
          {adminView ? 'Back to Users' : 'Sign Out'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.colors.pageCanvas,
        position: 'relative',
      }}
    >
      {/* Fixed header — matches HomePage Header.js (all breakpoints) */}
      <Box component="header" className="header-glass" sx={headerBarSx}>
        <Box sx={{ ...headerRowSx, minWidth: 0 }} className="header-container">
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }} className="logo-section">
            <Link to={`${basePath}/overview`} className="header-logo-link">
              <img src={logoImg} alt="Spotless AI Logo" className="header-logo-image" />
            </Link>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, justifyContent: 'flex-end', minWidth: 0, flexShrink: 0 }} className="actions-section">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '6px 12px',
                borderRadius: '16px',
                cursor: 'default',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                background: 'rgba(248, 250, 252, 0.5)',
                minWidth: 0,
                maxWidth: '100%',
                '&:hover': {
                  background: 'rgba(248, 250, 252, 0.8)',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                },
              }}
              className="user-profile"
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  flexShrink: 0,
                  background: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)',
                }}
              >
                {displayName ? displayName.charAt(0).toUpperCase() : <FiUser size={20} />}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, overflow: 'hidden' }} className="user-info">
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem', lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayRole}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Menu" placement="bottom">
              <IconButton onClick={toggleSidebar(true)} sx={menuButtonSx} className="focusable" aria-label="Open menu">
                <FiMenu size={24} color="white" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box className="gradient-line" sx={{ height: '4px', width: '100%', background: 'linear-gradient(to right, #3B82F6 0%, #EC4899 100%)', position: 'relative', overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
              animation: 'shimmer 3s infinite',
            }}
          />
        </Box>
      </Box>

      {/* Reserves space under fixed header — same role as Header.js `headerSpacer` */}
      <Box sx={{ height: HEADER_OFFSET }} />

      {/* Navigation drawer — same open/close pattern as HomePage Header.js (anchor right, menu + backdrop + X) */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={toggleSidebar(false)}
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            width: { xs: '100vw', sm: `${DRAWER_WIDTH}px` },
          },
        }}
      >
        <Box
          className="sidebar-container sidebar-glass"
          sx={{
            width: '100%',
            height: '100%',
            maxWidth: '100%',
          }}
        >
          {sidebarInner(true)}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 1,
          minHeight: `calc(100vh - ${HEADER_OFFSET})`,
        }}
      >
        {isUserAppRoute ? (
          <Box sx={{ width: '100%', minHeight: `calc(100vh - ${HEADER_OFFSET})`, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
            <Outlet context={outletContext} />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
            {adminView ? (
              <Box
                className="udb-admin-banner"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 2,
                  px: { xs: 1.5, sm: 2 },
                  py: 1.25,
                  borderRadius: '12px',
                  border: '1px solid rgba(99, 102, 241, 0.25)',
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(59,130,246,0.08) 100%)',
                }}
              >
                <Typography sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, color: '#4338ca', fontWeight: 600, lineHeight: 1.4 }}>
                  Viewing {displayName || routeEmail} · Read-only admin view
                </Typography>
                <Button
                  startIcon={<FiArrowLeft size={14} />}
                  onClick={handleExit}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#4f46e5',
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    px: { xs: 0, sm: 1 },
                  }}
                >
                  Back to Users
                </Button>
              </Box>
            ) : null}
            <Box
              sx={{
                borderRadius: `${theme.radius.lg}px`,
                border: `1px solid ${theme.colors.borderDefault}`,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                bgcolor: theme.colors.bgPrimary,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  pt: 3,
                  pb: 2,
                  borderBottom: `1px solid ${theme.colors.borderDefault}`,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'flex-start' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#888888', mb: 0.5, letterSpacing: '0.02em' }}>
                    Spotless AI - Client Dashboard
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 20, sm: 22, md: 28 }, fontWeight: 700, color: '#333333', lineHeight: 1.2, wordBreak: 'break-word' }}>
                    {displayName || hospitalInfo?.name || 'Hospital'}
                    {activeNavItem?.label ? ` · ${activeNavItem.label}` : ''}
                  </Typography>
                </Box>
                {/* Mobile: one centered tab + arrows. Desktop: full tab list. */}
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  <IconButton
                    aria-label="Previous section"
                    onClick={() => goToAdjacentTab(-1)}
                    disabled={!canGoPrevTab}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      color: '#64748b',
                      border: '1px solid #DDDDDD',
                      borderRadius: '8px',
                      bgcolor: '#fff',
                      width: 36,
                      height: 36,
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <FiChevronLeft size={18} />
                  </IconButton>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        color: '#333333',
                        textAlign: 'center',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '8px',
                        bgcolor: '#fff',
                        border: '1px solid #DDDDDD',
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                        lineHeight: 1.3,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {activeHeaderNavItem?.label || 'Overview'}
                    </Typography>
                  </Box>
                  <IconButton
                    aria-label="Next section"
                    onClick={() => goToAdjacentTab(1)}
                    disabled={!canGoNextTab}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      color: '#64748b',
                      border: '1px solid #DDDDDD',
                      borderRadius: '8px',
                      bgcolor: '#fff',
                      width: 36,
                      height: 36,
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <FiChevronRight size={18} />
                  </IconButton>
                </Box>
                <Box
                  className="udb-nav-tabs"
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexWrap: 'wrap',
                    gap: 0.75,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  {headerNav.map((item) => (
                    <Button
                      key={item.path}
                      component={NavLink}
                      to={item.path}
                      end={item.sub === 'overview'}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 2,
                        py: 0.75,
                        minWidth: 'auto',
                        flexShrink: 0,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#888888',
                        border: '1px solid transparent',
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                        '&.active': {
                          bgcolor: '#fff',
                          color: '#333333',
                          borderColor: '#DDDDDD',
                          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
              </Box>
              <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
                <Outlet context={outletContext} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
